/**
 * Date utility functions
 */
import { date } from "quasar"

const { formatDate } = date

/**
 * Gets today at 12am UTC
 * @returns Date object representing today at 12am UTC
 */
export const getTodayAtMidnightUTC = (): Date => {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

/**
 * Gets a date that is N days ago from the reference date
 * @param daysAgo Number of days to go back
 * @param referenceDate Reference date (defaults to today at midnight UTC)
 * @returns Date object representing the date N days ago
 */
export const getDateDaysAgo = (daysAgo: number, referenceDate: Date = getTodayAtMidnightUTC()): Date => {
  const startDate = new Date(referenceDate)
  startDate.setDate(startDate.getDate() - daysAgo)
  return startDate
}

/**
 * Gets a date that is one year ago from the reference date
 * @param referenceDate Reference date (defaults to today)
 * @returns Date object representing the date one year ago
 */
export const getDateOneYearAgo = (referenceDate: Date = new Date()): Date => {
  const startDate = new Date(referenceDate)
  startDate.setFullYear(startDate.getFullYear() - 1)
  return startDate
}

/**
 * Check if a date is equal to today at midnight UTC
 * @param dateToCheck Date to check
 * @returns Boolean indicating if the date is today at midnight UTC
 */
export const isDateEqualToTodayMidnightUTC = (dateToCheck: Date): boolean => {
  const todayAtMidnightUTC = getTodayAtMidnightUTC()
  
  // Create a new Date to avoid mutating the input
  const dateToCompare = new Date(dateToCheck)
  dateToCompare.setHours(0, 0, 0, 0)
  
  return dateToCompare.getTime() === todayAtMidnightUTC.getTime()
}

/**
 * Calculate the number of days between two dates
 * @param startDate Start date
 * @param endDate End date
 * @returns Number of days difference
 */
export const getDaysDifference = (startDate: Date, endDate: Date): number => {
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

/**
 * Format date for date picker internal use (YYYY/MM/DD)
 * @param dateValue Date to format
 * @returns Formatted date string
 */
export const fmtDateForDatePickerValue = (dateValue: Date | number | string): string => {
  return formatDate(dateValue, "YYYY/MM/DD")
}

/**
 * Format date for date picker display (MM/DD/YYYY)
 * @param dateValue Date to format
 * @returns Formatted date string
 */
export const fmtDateForDatePickerDisplay = (dateValue: Date | number | string): string => {
  return formatDate(dateValue, "MM/DD/YYYY")
}
